import { lineString, polygon, point } from '@turf/helpers';
import { booleanEqual, distance, polygonSmooth } from '@turf/turf';

function markLoopsAndValidSegments(geojson, proximityThreshold = 0.0001) {
    if (!geojson) {
        console.error("Invalid GeoJSON structure.");
        return geojson;
    }

    const features = geojson.features || geojson;

    features.forEach(feature => {
        let coords = feature.geometry.coordinates;

        let loopDetected = false;
        let loopEndIndex = -1;

        // Step 1: Identify the loop, allowing a small distance between start and end
        const startPoint = point(coords[0]);

        for (let i = 1; i < coords.length; i++) {
            const currentPoint = point(coords[i]);
            const loopDistance = distance(startPoint, currentPoint);

            if (loopDistance < proximityThreshold) {
                loopDetected = true;
                loopEndIndex = i;
                break;
            }
        }

        // Step 2: If a loop is detected, isolate it and remove any points beyond the loop
        if (loopDetected) {
            coords = coords.slice(0, loopEndIndex + 1);

            // Mark the feature as a loop
            feature.geometry.coordinates = coords;
            feature.properties = feature.properties || {};
            feature.properties.user_portColor = '#00ff00';  // Green for loops
            feature.properties.type = "loop";
        } else {
            // If no loop is detected, mark it as a non-loop segment and remove it
            feature.geometry.coordinates = []; // Clear coordinates
            feature.properties = feature.properties || {};
            feature.properties.user_portColor = '#ff0000';  // Red for non-loops
            feature.properties.type = "valid segment";
        }
    });

    // Step 3: Filter out features with no coordinates (i.e., removed segments)
    const filteredFeatures = features.filter(feature => feature.geometry.coordinates.length > 0);

    return {
        type: "FeatureCollection",
        features: filteredFeatures
    };
}

export const removeAreaFeatures = (geojson) => {
    if (!geojson || !geojson.features) {
        console.error("Invalid GeoJSON structure.");
        return geojson;
    }

    // Filter out features that have the property "area" set to "yes"
    const filteredFeatures = geojson.features.filter(feature => {
        return feature.properties.area !== "yes";
    });

    // Return a new GeoJSON object with the filtered features
    return {
        ...geojson,
        features: filteredFeatures
    };
};


export const generateGeoJSON = (osmData, nodes, applySmoothing = false) => {
    const geojson = {
      type: "FeatureCollection",
      features: []
    };
  
    // Step 1: Create initial LineString features
    let lineStrings = osmData
      .filter(element => element.type === 'way')
      .map(element => {
        const coordinates = element.nodes
          .map(nodeId => {
            const node = nodes[nodeId];
            return node ? [node.lon, node.lat] : null;
          })
          .filter(coord => coord !== null);
  
        return coordinates.length >= 2 ? lineString(coordinates, element.tags) : null;
      })
      .filter(ls => ls !== null);
  
    console.log("LineStrings:", { type: "FeatureCollection", features: lineStrings });
  
    // Step 2: Iteratively merge connected LineStrings
    let mergedLineStrings = iterativeMergeLineStrings(lineStrings);
  
    console.log("Merged LineStrings:", { type: "FeatureCollection", features: mergedLineStrings });
  
    // Step 3: Filter to keep only closed loops
    const loopFeatures = markLoopsAndValidSegments(mergedLineStrings);
  
    // Step 4: Remove unnecessary area features
    const loopsCleaned = removeAreaFeatures(loopFeatures);
  
    let smoothedLineStrings;
  
    if (applySmoothing) {
      // Step 5: Convert loops to polygons for smoothing
      const polygons = loopsCleaned.features.map(loop => {
        if (loop.geometry && Array.isArray(loop.geometry.coordinates)) {
          const coordinates = loop.geometry.coordinates;
  
          // Ensure the LineString is closed (first and last points are the same)
          if (coordinates.length > 0 && (coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
              coordinates[0][1] !== coordinates[coordinates.length - 1][1])) {
            coordinates.push(coordinates[0]); // Close the loop
          }
  
          return polygon([coordinates], loop.properties);
        } else {
          console.warn("Invalid geometry structure in loop, skipping:", loop);
          return null;
        }
      }).filter(polygon => polygon !== null); // Filter out any null values
  
      // Step 6: Apply smoothing to Polygons
      const smoothedPolygons = polygons.map(poly => polygonSmooth(poly, { iterations: 3 }));
  
      // Step 7: Convert smoothed polygons back to LineStrings
      smoothedLineStrings = smoothedPolygons
        .map(collection => {
          if (collection.type === "FeatureCollection" && Array.isArray(collection.features)) {
            return collection.features.map(feature => {
              if (feature.geometry && Array.isArray(feature.geometry.coordinates)) {
                const coordinates = feature.geometry.coordinates[0]; // Use the first ring of the polygon
                return lineString(coordinates, feature.properties);
              } else {
                console.warn("Invalid geometry structure in feature, skipping:", feature);
                return null;
              }
            }).filter(ls => ls !== null); // Filter out any null values
          } else {
            console.warn("Invalid FeatureCollection structure, skipping:", collection);
            return null;
          }
        })
        .flat() // Flatten the array of arrays into a single array of LineStrings
        .filter(ls => ls !== null); // Filter out any null values
    } else {
      smoothedLineStrings = loopsCleaned.features.map(loop => loop);
    }
  
    // Step 8: Add smoothed loops to the GeoJSON
    smoothedLineStrings.forEach(loop => {
      geojson.features.push(loop);
    });
  
    console.log("Generated GeoJSON with loops:", geojson);
  
    return geojson;
  };

  
function iterativeMergeLineStrings(lineStrings) {
    let merged = [];
    let changesMade = true;

    while (changesMade) {
        changesMade = false;
        const used = new Set();
        const tempMerged = [];

        for (let i = 0; i < lineStrings.length; i++) {
            if (used.has(i)) continue;

            let mergedLine = lineStrings[i];
            used.add(i);

            for (let j = i + 1; j < lineStrings.length; j++) {
                if (used.has(j)) continue;

                const coords1 = mergedLine.geometry.coordinates;
                const coords2 = lineStrings[j].geometry.coordinates;

                if (booleanEqual(point(coords1[coords1.length - 1]), point(coords2[0]))) {
                    // End of coords1 matches start of coords2
                    mergedLine.geometry.coordinates = coords1.concat(coords2.slice(1));
                    used.add(j);
                    changesMade = true;
                } else if (booleanEqual(point(coords1[0]), point(coords2[coords2.length - 1]))) {
                    // Start of coords1 matches end of coords2
                    mergedLine.geometry.coordinates = coords2.slice(0, coords2.length - 1).concat(coords1);
                    used.add(j);
                    changesMade = true;
                }
            }
            tempMerged.push(mergedLine);
        }

        merged = tempMerged;
        lineStrings = merged;
    }

    return merged;
}
